/**
 * InsightsModule - Aggregates Analytics + Feedback views
 * Visible for roles: admin, tuner (via Config + AccessControl)
 * 
 * REFACTORED: Now uses unified panel system for consistent UI
 */

import AnalyticsService from '../../services/AnalyticsService.js';
import FeedbackService from '../../services/FeedbackService.js';
import ApiService from '../../services/ApiService.js';
import PromptService from '../../services/PromptService.js';
import Config from '../../core/Config.js';
import { installSplit } from '../../utils/Splitter.js';
import { createLayoutPanel, createPanelGroup, createPanelActions, PANEL_VARIANTS, PANEL_LAYOUTS } from '../../utils/panel.js';

export class InsightsModule {
  constructor() {
    this.container = null;
  }

  async initialize() {
    try { FeedbackService.init(); } catch (_) {}
  }

  activate() {}

  mount(container) {
    this.container = container;
    this.render();
  }

  render() {
    if (!this.container) return;
    const session = AnalyticsService.getActiveSession?.();
    const counts = AnalyticsService.getEventCounts?.() || {};
    const analyticsEnabled = (import.meta.env?.VITE_ENABLE_ANALYTICS === 'true') && (localStorage.getItem('enableAnalytics') === 'true');
    
    // Build Analytics panel content
    const analyticsContent = `
      <div class="text-muted" style="margin-bottom:8px">Local telemetry only; no network calls.</div>
      <div style="margin-bottom:8px">
        ${analyticsEnabled ? '<span class="badge" style="background:#DCFCE7;color:#166534;padding:4px 8px;border-radius:6px">Enabled</span>' : '<span class="badge" style="background:#FEE2E2;color:#991B1B;padding:4px 8px;border-radius:6px">Disabled</span>'}
      </div>
      <div class="grid" style="display:grid;grid-template-columns:repeat(3,1fr);gap:12px">
        <div class="card"><div class="label">Active Session</div><div class="value">${session ? 'Yes' : 'No'}</div></div>
        <div class="card"><div class="label">Events</div><div class="value">${counts.total || 0}</div></div>
        <div class="card"><div class="label">Prompts Sent</div><div class="value">${counts.prompts || 0}</div></div>
      </div>
      <div style="margin-top:12px">
        <button class="btn btn-primary btn-small" data-action="analytics-export">Export JSON</button>
        <button class="btn btn-ghost btn-small" data-action="analytics-clear">Clear</button>
      </div>
    `;
    
    // Build Feedback panel content
    const feedbackContent = `
      <div class="text-muted" style="margin-bottom:8px">Store quick feedback locally for pilot triage.</div>
      <div style="display:flex;gap:8px;align-items:center;margin-bottom:8px">
        <select id="fb-category" class="form-control" style="max-width:200px">
          <option value="ux">UX</option>
          <option value="bug">Bug</option>
          <option value="idea">Idea</option>
          <option value="other">Other</option>
        </select>
        <input id="fb-contact" class="form-control" placeholder="Contact (optional)" style="max-width:220px"/>
      </div>
      <textarea id="fb-text" rows="3" class="form-control" placeholder="Write feedback..."></textarea>
      <div style="margin-top:8px">
        <label style="display:inline-flex;align-items:center;gap:6px"><input type="checkbox" id="fb-diag"/> Include diagnostics</label>
        <button class="btn btn-primary btn-small" data-action="feedback-submit" style="margin-left:8px">Submit</button>
      </div>
      <div id="fb-list" style="margin-top:12px"></div>
    `;
    
    // Build Feedback Analysis panel content
    const analysisContent = `
      <div class="text-muted" style="margin-bottom:8px">Summarize user feedback when analytics is enabled.</div>
      <div style="display:flex;gap:8px;align-items:center;margin-bottom:8px">
        <button class="btn btn-primary btn-small" data-action="analyze-feedback" ${analyticsEnabled ? '' : 'disabled'}>Summarize Feedback</button>
        ${analyticsEnabled ? '' : '<small class="text-secondary">Enable in Settings → Feedback & Analytics</small>'}
      </div>
      <div id="fb-summary" class="text-block" style="white-space:pre-wrap"></div>
    `;
    
    // Create panels using layout-safe panels for split layout
    const analyticsPanel = createLayoutPanel({
      title: 'Analytics (local)',
      variant: PANEL_VARIANTS.DEFAULT,
      content: analyticsContent
    });
    
    const feedbackPanel = createLayoutPanel({
      title: 'Feedback',
      variant: PANEL_VARIANTS.DEFAULT,
      content: feedbackContent
    });
    
    const analysisPanel = createLayoutPanel({
      title: 'Feedback Analysis',
      variant: PANEL_VARIANTS.DEFAULT,
      content: analysisContent
    });
    
    // Create left column with stacked panels
    const leftColumn = `
      <div class="panel-group panel-group--stack">
        ${analyticsPanel}
        ${feedbackPanel}
      </div>
    `;
    
    // Render with split layout
    this.container.innerHTML = `
      <div class="insights-module">
        <div class="insights-split-layout panel-group panel-group--split">
          ${leftColumn}
          ${analysisPanel}
        </div>
      </div>
    `;

    this.attachHandlers();
    this.renderFeedbackList();

    // Install resizable split for Insights
    try { installSplit('.insights-split-layout', 'insights'); } catch (_) {}
  }

  attachHandlers() {
    const root = this.container;
    
    // Analytics handlers
    const exportBtn = root.querySelector('[data-action="analytics-export"]');
    if (exportBtn) {
      exportBtn.addEventListener('click', async () => {
        try {
          const data = await AnalyticsService.exportAll();
          const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; 
          a.download = 'analytics.json'; 
          a.click(); 
          URL.revokeObjectURL(url);
        } catch (_) {}
      });
    }
    
    const clearBtn = root.querySelector('[data-action="analytics-clear"]');
    if (clearBtn) {
      clearBtn.addEventListener('click', async () => {
        try { 
          await AnalyticsService.clearAll(); 
          this.render(); 
        } catch (_) {}
      });
    }
    
    // Feedback handlers
    const submitBtn = root.querySelector('[data-action="feedback-submit"]');
    if (submitBtn) {
      submitBtn.addEventListener('click', async () => {
        const cat = root.querySelector('#fb-category')?.value || 'ux';
        const text = root.querySelector('#fb-text')?.value?.trim();
        const contact = root.querySelector('#fb-contact')?.value?.trim() || '';
        const diag = !!root.querySelector('#fb-diag')?.checked;
        if (!text) return;
        try { 
          await FeedbackService.submitAppFeedback({ category: cat, text, contact, includeDiagnostics: diag }); 
        } catch (_) {}
        try { 
          root.querySelector('#fb-text').value = ''; 
        } catch(_) {}
        this.renderFeedbackList();
      });
    }
    
    // Analysis handler
    const analyzeBtn = root.querySelector('[data-action="analyze-feedback"]');
    if (analyzeBtn) {
      analyzeBtn.addEventListener('click', () => this.analyzeFeedback());
    }
  }

  async renderFeedbackList() {
    const box = this.container?.querySelector('#fb-list');
    if (!box) return;
    try {
      const { db } = await import('../../services/db/DexieDB.js');
      const apps = await db?.app_feedback?.orderBy('ts')?.reverse()?.limit(20)?.toArray();
      const msgs = await db?.message_feedback?.orderBy('ts')?.reverse()?.limit(20)?.toArray();
      const appList = Array.isArray(apps) && apps.length
        ? `<h4 style="margin:8px 0">Recent App Feedback</h4><ul class="list">${apps.map(r => `<li><strong>[${r.category}]</strong> ${r.text || r.comment || ''}</li>`).join('')}</ul>`
        : '<div class="text-muted">No app feedback yet.</div>';
      const msgList = Array.isArray(msgs) && msgs.length
        ? `<h4 style="margin:8px 0">Recent Message Feedback</h4><ul class="list">${msgs.map(r => `<li>${r.vote ? (r.vote === 'up' ? '👍' : (r.vote === 'down' ? '👎' : '•')) : '•'} ${r.rating ? `(${r.rating}/5)` : ''} ${r.comment || ''}</li>`).join('')}</ul>`
        : '<div class="text-muted">No message feedback yet.</div>';
      box.innerHTML = appList + '<hr style="margin:12px 0">' + msgList;
    } catch (_) {
      box.innerHTML = '<div class="text-muted">No feedback storage available.</div>';
    }
  }

  async analyzeFeedback() {
    try {
      const { db } = await import('../../services/db/DexieDB.js');
      const apps = await db?.app_feedback?.toArray();
      const msgs = await db?.message_feedback?.toArray();
      const byCat = (apps || []).reduce((m, r) => { const k = r.category || 'other'; m[k] = (m[k]||0)+1; return m; }, {});
      const votes = (msgs || []).reduce((m, r) => { const k = r.vote || 'none'; m[k] = (m[k]||0)+1; return m; }, {});
      const ratings = (msgs || []).filter(r => Number.isFinite(r.rating)).map(r => r.rating);
      const avgRating = ratings.length ? (ratings.reduce((a,b)=>a+b,0)/ratings.length).toFixed(2) : null;
      const sample = (apps || []).slice(-10).map(r => ({ category: r.category, text: r.text || r.comment || '' }));
      const context = { counts: { appFeedback: (apps||[]).length, messageFeedback: (msgs||[]).length }, byCategory: byCat, votes, avgRating, sample };
      const promptKey = 'metricsAssistant';
      const messages = PromptService.buildMessages(`Summarize user feedback. Focus on themes and suggested actions.\n\nCONTEXT:\n\n\`\`\`json\n${JSON.stringify(context)}\n\`\`\``, promptKey, []);
      const apiConfig = PromptService.getApiConfig(promptKey);
      const res = await ApiService.call(messages[messages.length - 1].content, {
        systemRole: messages[0].content,
        history: [],
        temperature: apiConfig.temperature,
        maxTokens: apiConfig.maxTokens,
        promptKey: apiConfig.promptKey,
        promptVersion: apiConfig.promptVersion
      });
      const out = String(res?.content || '').trim();
      const target = this.container?.querySelector('#fb-summary');
      if (target) target.textContent = out || 'No analysis available.';
    } catch (e) {
      const target = this.container?.querySelector('#fb-summary');
      if (target) target.textContent = 'Could not analyze feedback.';
    }
  }
}

export default InsightsModule;
